/**
 * RealtimeTasksContext — provides app-wide real-time task subscription status.
 *
 * **Part of Phase 2d of the Realtime ↔ Mycelium plan** (consolidation).
 *
 * The previous implementation opened its own `supabase.channel()` with three
 * separate `.on('postgres_changes', …)` listeners (INSERT / UPDATE / DELETE),
 * manually spliced the React Query cache on every event, and reimplemented
 * exponential-backoff reconnection. The new implementation delegates the
 * subscription to the canonical {@link useRealtimeTable} hook, which is backed
 * by `unified-realtime.service.ts` (de-dup, channel reuse, connection-health
 * tracking, and metrics).
 *
 * The provider + context pattern is **preserved** — consumers still call
 * `useRealtimeTasks()` and receive `{ isConnected, retryCount, reconnect }`.
 * The `reconnect` method is now a no-op (with a dev warning) because the
 * unified service handles reconnection automatically.
 */

import { useAuth } from "@/hooks/useAuth";
import { useRealtimeTable } from "@/hooks/useRealtimeTable";
import { queryKeys } from "@/lib/queryClient";
import { logger } from "@/lib/logger";
import { createContext } from "react";

// ============================================================================
// CONTEXT
// ============================================================================

interface RealtimeTasksContextValue {
  isConnected: boolean;
  retryCount: number;
  reconnect: () => void;
}

export const RealtimeTasksContext =
  createContext<RealtimeTasksContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface RealtimeTasksProviderProps {
  children: React.ReactNode;
  /** @deprecated Ignored since Phase 2d — the unified service handles retry. */
  maxRetries?: number;
  /** @deprecated Ignored since Phase 2d — the unified service handles retry. */
  retryDelay?: number;
}

export const RealtimeTasksProvider: React.FC<RealtimeTasksProviderProps> = ({
  children,
}) => {
  const { user } = useAuth();

  const { isSubscribed, error } = useRealtimeTable("todos", {
    enabled: Boolean(user?.id),
    invalidateKeys: [
      queryKeys.todos.lists(),
      ["todo-statistics"],
      queryKeys.stats.all,
    ],
    onChange: (payload) => {
      logger.info("🔔 Real-time task update received", {
        eventType: payload.eventType,
      });
    },
  });

  const value: RealtimeTasksContextValue = {
    isConnected: isSubscribed,
    retryCount: error ? 1 : 0,
    reconnect: () => {
      if (process.env.NODE_ENV !== "production") {
        logger.warn(
          "[RealtimeTasksProvider] reconnect() is a no-op since Phase 2d — the unified service handles reconnection automatically.",
        );
      }
    },
  };

  return (
    <RealtimeTasksContext.Provider value={value}>
      {children}
    </RealtimeTasksContext.Provider>
  );
};
